import { Canvas } from '@/components/Canvas/Canvas'
import { RelationsLayer } from '@/components/Canvas/RelationsLayer'
import { CanvasContextMenu } from '@/components/ContextMenu/CanvasContextMenu'
import { TableContextMenu } from '@/components/ContextMenu/TableContextMenu'
import { useTableEditorModal } from '@/hooks/useTableEditorModal'
import { RelationHandles } from '@/components/Relation/RelationHandles'
import { Table } from '@/components/Table/Table'
import { useContextMenu } from '@/hooks/useContextMenu'
import { useModalStore } from '@/hooks/useModalStore'
import { useRelationCreation } from '@/hooks/useRelationCreation'
import { useDiagramStore, useSelectionStore } from '@/stores'
import { useDraftStore } from '@/stores/draftStore'
import { useTabViewStore } from '@/stores/tabViewStore'

type CanvasAreaContextMenuData =
  | {
      kind: 'canvas'
      canvasX: number
      canvasY: number
    }
  | {
      kind: 'table'
      tableId: string
    }

export function CanvasArea() {
  const tables  = useDiagramStore((s) => s.tables)
  const relationships = useDiagramStore((s) => s.rels)
  const groups  = useDiagramStore((s) => s.groups)
  const hiddenTableIds = useTabViewStore((s) => s.hiddenTableIds)
  const selectedTableId  = useSelectionStore((s) => s.selectedTableId)
  const selectedTableIds = useSelectionStore((s) => s.selectedTableIds)
  const selectedRelationshipId = useSelectionStore((s) => s.selectedRelationshipId)
  const relSrcId         = useSelectionStore((s) => s.relationshipSourceTableId)
  const readOnly = useDraftStore((s) => s.mode === 'view')
  const { handleTableClick, startRelationshipFromTable } = useRelationCreation()
  const { menu, closeContextMenu, openContextMenu } = useContextMenu<CanvasAreaContextMenuData>()
  const openEdit = useTableEditorModal((state) => state.openEdit)
  const openFkPanel = useModalStore((state) => state.openFkPanel)
  const openIndexPanel = useModalStore((state) => state.openIndexPanel)
  const hiddenTableIdSet = new Set(hiddenTableIds)
  const visibleTables = tables.filter((table) => !hiddenTableIdSet.has(table.id))
  const visibleTableIds = new Set(visibleTables.map((table) => table.id))
  const visibleRelationships = relationships.filter(
    (relationship) =>
      visibleTableIds.has(relationship.from) &&
      visibleTableIds.has(relationship.to),
  )

  return (
    <>
      <Canvas
        onCanvasContextMenu={({ clientX, clientY, canvasX, canvasY }) => {
          openContextMenu(clientX, clientY, {
            kind: 'canvas',
            canvasX,
            canvasY,
          })
        }}
      >
      <RelationsLayer
        relationships={visibleRelationships}
        tables={visibleTables}
        groups={groups}
        readOnly={readOnly}
      />

      {visibleTables.map((table) => (
        <Table
          key={table.id}
          table={table}
          allTables={tables}
          groups={groups}
          isSelected={selectedTableId === table.id}
          isMultiSelected={selectedTableIds.size > 1 && selectedTableIds.has(table.id)}
          isRelSrc={relSrcId === table.id}
          onTableClick={handleTableClick}
          onRequestEdit={readOnly ? undefined : openEdit}
          onOpenFkPanel={(tableId, event) =>
            !readOnly && openFkPanel(tableId, {
              x: event.currentTarget.getBoundingClientRect().left,
              y: event.currentTarget.getBoundingClientRect().bottom,
            })}
          onOpenIndexPanel={(tableId, event) =>
            !readOnly && openIndexPanel(tableId, {
              x: event.currentTarget.getBoundingClientRect().left,
              y: event.currentTarget.getBoundingClientRect().bottom,
            })}
          onTableContextMenu={(tableId, event) => {
            useSelectionStore.getState().selectTable(tableId)
            openContextMenu(event.clientX, event.clientY, {
              kind: 'table',
              tableId,
            })
          }}
        />
      ))}

      <RelationHandles
        relationships={visibleRelationships}
        tables={visibleTables}
        selectedRelationshipId={selectedRelationshipId}
        readOnly={readOnly}
      />
      </Canvas>

      {menu?.data.kind === 'canvas' ? (
        <CanvasContextMenu
          open
          x={menu.x}
          y={menu.y}
          canvasX={menu.data.canvasX}
          canvasY={menu.data.canvasY}
          onClose={closeContextMenu}
          readOnly={readOnly}
        />
      ) : null}

      {menu?.data.kind === 'table' ? (
        <TableContextMenu
          open
          x={menu.x}
          y={menu.y}
          tableId={menu.data.tableId}
          onClose={closeContextMenu}
          onStartRelationship={(tableId, type) => {
            if (!readOnly) startRelationshipFromTable(tableId, type)
          }}
          readOnly={readOnly}
        />
      ) : null}
    </>
  )
}
